<template>
  <div class="attendance-page">
    <el-container>
      <!-- 顶部导航 -->
      <el-header class="header">
        <div class="header-left">
          <h2>工人端 - 永盛制冷维修有限公司</h2>
        </div>
        <div class="header-right">
          <el-dropdown @command="handleCommand">
            <span class="user-info">
              <i class="el-icon-user"></i>
              {{ currentUser.name }}
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">个人信息</el-dropdown-item>
              <el-dropdown-item command="home">返回首页</el-dropdown-item>
              <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 公告栏 -->
      <AnnouncementBar />

      <!-- 主内容区 -->
      <el-main class="main-content">
        <div class="page-header">
          <div class="header-content">
            <div class="header-text">
              <h1>考勤打卡</h1>
              <p>记录您的工作时间</p>
            </div>
            <div class="header-actions">
              <el-button 
                type="primary" 
                icon="el-icon-s-home"
                @click="$router.push('/worker/home')"
                class="home-btn"
              >
                返回首页
              </el-button>
            </div>
          </div>
        </div>

        <!-- 今日打卡状态 -->
        <el-card class="today-status-card">
          <div slot="header" class="card-header">
            <span>今日打卡状态</span>
            <span class="date">{{ currentDate }}</span>
          </div>

          <div class="clock-section">
            <div class="current-time">
              <div class="time-display">{{ currentTime }}</div>
              <div class="date-display">{{ currentDateDisplay }}</div>
            </div>

            <div class="clock-status">
              <div class="status-item" :class="{ 'completed': todayStatus.hasCheckedIn }">
                <div class="status-icon">
                  <i class="el-icon-time" v-if="!todayStatus.hasCheckedIn"></i>
                  <i class="el-icon-success" v-else></i>
                </div>
                <div class="status-info">
                  <div class="status-label">签到</div>
                  <div class="status-time">
                    {{ todayStatus.checkInTime || '--:--' }}
                  </div>
                </div>
              </div>

              <div class="status-divider"></div>

              <div class="status-item" :class="{ 'completed': todayStatus.hasCheckedOut }">
                <div class="status-icon">
                  <i class="el-icon-time" v-if="!todayStatus.hasCheckedOut"></i>
                  <i class="el-icon-success" v-else></i>
                </div>
                <div class="status-info">
                  <div class="status-label">签退</div>
                  <div class="status-time">
                    {{ todayStatus.checkOutTime || '--:--' }}
                  </div>
                </div>
              </div>
            </div>

            <div class="work-summary" v-if="todayStatus.hasCheckedIn">
              <div class="summary-item">
                <span class="label">工作时长:</span>
                <span class="value">{{ todayStatus.workHours.toFixed(1) }}小时</span>
              </div>
              <div class="summary-item" v-if="todayStatus.overtimeHours > 0">
                <span class="label">加班时长:</span>
                <span class="value overtime">{{ todayStatus.overtimeHours.toFixed(1) }}小时</span>
              </div>
            </div>

            <div class="clock-actions">
              <el-button 
                type="primary" 
                size="large"
                @click="showClockDialog('in')"
                :disabled="todayStatus.hasCheckedIn"
                v-if="!todayStatus.hasCheckedIn"
              >
                <i class="el-icon-time"></i>
                签到
              </el-button>

              <el-button 
                type="warning" 
                size="large"
                @click="showClockDialog('out')"
                :disabled="!todayStatus.hasCheckedIn || todayStatus.hasCheckedOut"
                v-if="todayStatus.hasCheckedIn && !todayStatus.hasCheckedOut"
              >
                <i class="el-icon-time"></i>
                签退
              </el-button>

              <div class="completed-message" v-if="todayStatus.hasCheckedOut">
                <i class="el-icon-circle-check"></i>
                今日打卡已完成
              </div>
            </div>
          </div>
        </el-card>

        <!-- 考勤记录 -->
        <el-card class="records-card">
          <div slot="header" class="card-header">
            <span>考勤记录</span>
            <div class="header-controls">
              <el-date-picker
                v-model="selectedMonth"
                type="month"
                placeholder="选择月份"
                format="yyyy-MM"
                value-format="yyyy-MM"
                @change="loadAttendanceRecords"
                size="small">
              </el-date-picker>
            </div>
          </div>

          <div class="records-summary">
            <div class="summary-card">
              <div class="summary-number">{{ monthlyStats.presentDays }}</div>
              <div class="summary-label">出勤天数</div>
            </div>
            <div class="summary-card">
              <div class="summary-number">{{ monthlyStats.totalWorkHours.toFixed(1) }}</div>
              <div class="summary-label">工作时长(小时)</div>
            </div>
            <div class="summary-card">
              <div class="summary-number">{{ monthlyStats.overtimeHours.toFixed(1) }}</div>
              <div class="summary-label">加班时长(小时)</div>
            </div>
          </div>

          <el-table 
            :data="attendanceRecords" 
            v-loading="loading"
            style="width: 100%"
            empty-text="暂无考勤记录"
          >
            <el-table-column prop="date" label="日期" width="120">
              <template slot-scope="scope">
                {{ formatDate(scope.row.date) }}
              </template>
            </el-table-column>
            <el-table-column prop="check_in_time" label="签到时间" width="100">
              <template slot-scope="scope">
                {{ scope.row.check_in_time || '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="check_out_time" label="签退时间" width="100">
              <template slot-scope="scope">
                {{ scope.row.check_out_time || '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="work_hours" label="工作时长" width="100">
              <template slot-scope="scope">
                {{ scope.row.work_hours ? scope.row.work_hours + '小时' : '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="overtime_hours" label="加班时长" width="100">
              <template slot-scope="scope">
                <span v-if="scope.row.overtime_hours > 0" class="overtime">
                  {{ scope.row.overtime_hours }}小时
                </span>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80">
              <template slot-scope="scope">
                <el-tag 
                  :type="getStatusType(scope.row.status)"
                  size="mini"
                >
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="notes" label="备注" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.notes || '--' }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-main>
    </el-container>

    <!-- 打卡对话框 -->
    <el-dialog
      :title="clockType === 'in' ? '签到' : '签退'"
      :visible.sync="clockDialogVisible"
      width="400px"
      center
    >
      <div class="clock-dialog-content">
        <div class="current-time-large">{{ currentTime }}</div>
        <div class="current-date-large">{{ currentDateDisplay }}</div>
        
        <el-form :model="clockForm" style="margin-top: 20px;">
          <el-form-item label="备注">
            <el-input
              v-model="clockForm.notes"
              type="textarea"
              :rows="3"
              :placeholder="clockType === 'in' ? '签到备注（可选）' : '签退备注（可选）'"
              maxlength="200"
              show-word-limit
            ></el-input>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="clockDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="confirmClock"
          :loading="clockSubmitting"
        >
          确认{{ clockType === 'in' ? '签到' : '签退' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'WorkerAttendance',
  data() {
    return {
      loading: false,
      currentTime: '',
      currentDate: '',
      currentDateDisplay: '',
      currentUser: {
        name: localStorage.getItem('userName') || '用户'
      },
      
      // 今日状态
      todayStatus: {
        hasCheckedIn: false,
        hasCheckedOut: false,
        checkInTime: null,
        checkOutTime: null,
        workHours: 0,
        overtimeHours: 0
      },
      
      // 考勤记录
      attendanceRecords: [],
      selectedMonth: '',
      
      // 月度统计
      monthlyStats: {
        presentDays: 0,
        totalWorkHours: 0,
        overtimeHours: 0
      },
      
      // 打卡对话框
      clockDialogVisible: false,
      clockType: 'in', // 'in' | 'out'
      clockSubmitting: false,
      clockForm: {
        notes: ''
      },
      
      // 定时器
      timeTimer: null
    }
  },
  async created() {
    this.initializeData()
    this.startTimeUpdater()
    await this.loadTodayStatus()
    await this.loadAttendanceRecords()
  },
  beforeDestroy() {
    if (this.timeTimer) {
      clearInterval(this.timeTimer)
    }
  },
  methods: {
    // 初始化数据
    initializeData() {
      const now = new Date()
      this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      this.updateCurrentTime()
    },
    
    // 启动时间更新器
    startTimeUpdater() {
      this.timeTimer = setInterval(() => {
        this.updateCurrentTime()
      }, 1000)
    },
    
    // 更新当前时间
    updateCurrentTime() {
      const now = new Date()
      this.currentTime = now.toTimeString().slice(0, 8)
      this.currentDate = now.toISOString().slice(0, 10)
      this.currentDateDisplay = now.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })
    },
    
    // 加载今日状态
    async loadTodayStatus() {
      try {
        const response = await this.$http.get('/api/attendance/today-status')
        if (response.success) {
          this.todayStatus = response.data
        }
      } catch (error) {
        console.error('加载今日状态失败:', error)
      }
    },
    
    // 加载考勤记录
    async loadAttendanceRecords() {
      try {
        this.loading = true
        const [year, month] = this.selectedMonth.split('-')
        const response = await this.$http.get('/api/attendance/my-records', {
          params: { year, month }
        })
        
        if (response.success) {
          this.attendanceRecords = response.data
          this.calculateMonthlyStats()
        }
      } catch (error) {
        console.error('加载考勤记录失败:', error)
        this.$message.error('加载考勤记录失败')
      } finally {
        this.loading = false
      }
    },
    
    // 计算月度统计
    calculateMonthlyStats() {
      this.monthlyStats = {
        presentDays: this.attendanceRecords.filter(r => r.status === 'present').length,
        totalWorkHours: this.attendanceRecords.reduce((sum, r) => sum + (parseFloat(r.work_hours) || 0), 0),
        overtimeHours: this.attendanceRecords.reduce((sum, r) => sum + (parseFloat(r.overtime_hours) || 0), 0)
      }
    },
    
    // 显示打卡对话框
    showClockDialog(type) {
      this.clockType = type
      this.clockForm.notes = ''
      this.clockDialogVisible = true
    },
    
    // 确认打卡
    async confirmClock() {
      try {
        this.clockSubmitting = true
        
        const response = await this.$http.post('/api/attendance/clock', {
          type: this.clockType,
          notes: this.clockForm.notes
        })
        
        if (response.success) {
          this.$message.success(response.message)
          this.clockDialogVisible = false
          await this.loadTodayStatus()
          await this.loadAttendanceRecords()
        } else {
          this.$message.error(response.message)
        }
      } catch (error) {
        console.error('打卡失败:', error)
        this.$message.error('打卡失败，请重试')
      } finally {
        this.clockSubmitting = false
      }
    },
    
    // 格式化日期
    formatDate(dateStr) {
      const date = new Date(dateStr)
      const today = new Date()
      
      if (date.toDateString() === today.toDateString()) {
        return '今天'
      }
      
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return '昨天'
      }
      
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit'
      })
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      const types = {
        present: 'success',
        absent: 'danger',
        late: 'warning',
        leave: 'info'
      }
      return types[status] || 'info'
    },
    
    // 获取状态文本
    getStatusText(status) {
      const texts = {
        present: '出勤',
        absent: '缺勤',
        late: '迟到',
        leave: '请假'
      }
      return texts[status] || status
    },
    
    // 移动端菜单
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    
    handleMenuClick() {
      this.closeMobileMenu()
    },
    
    // 处理顶部菜单命令
    handleCommand(command) {
      switch(command) {
        case 'profile':
          this.$router.push('/worker/profile')
          break
        case 'home':
          this.$router.push('/worker/home')
          break
        case 'logout':
          this.$store.dispatch('logout')
          this.$message.success('退出登录成功')
          this.$router.push('/login')
          break
      }
    }
  }
}
</script>

<style scoped>
.attendance-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.worker-sidebar {
  background-color: #2c3e50;
  min-height: 100vh;
}

.worker-main {
  background: transparent;
  padding: 20px;
  position: relative;
}

.mobile-menu-btn {
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.page-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-text h1 {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(103, 194, 58, 0.3);
  letter-spacing: 1px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.header-text p {
  color: #6c757d;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.home-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #409EFF;
  font-weight: bold;
  border-radius: 25px;
  padding: 12px 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.home-btn:hover,
.home-btn:focus {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.today-status-card {
  margin-bottom: 30px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 18px;
}

.date {
  color: #909399;
  font-size: 14px;
}

.clock-section {
  padding: 20px 0;
}

.current-time {
  text-align: center;
  margin-bottom: 30px;
}

.time-display {
  font-size: 48px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 10px;
  font-family: 'Courier New', monospace;
}

.date-display {
  font-size: 16px;
  color: #666;
}

.clock-status {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 15px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.status-item.completed {
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  color: white;
}

.status-icon {
  font-size: 24px;
  margin-bottom: 10px;
}

.status-label {
  font-size: 14px;
  margin-bottom: 5px;
}

.status-time {
  font-size: 18px;
  font-weight: bold;
  font-family: 'Courier New', monospace;
}

.status-divider {
  width: 40px;
  height: 2px;
  background: #e4e7ed;
  margin: 0 20px;
}

.work-summary {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
  padding: 20px;
  background: #f0f9ff;
  border-radius: 10px;
}

.summary-item {
  text-align: center;
}

.summary-item .label {
  color: #666;
  font-size: 14px;
}

.summary-item .value {
  color: #409EFF;
  font-weight: bold;
  font-size: 18px;
  margin-left: 10px;
}

.summary-item .value.overtime {
  color: #f56c6c;
}

.clock-actions {
  text-align: center;
}

.completed-message {
  color: #52c41a;
  font-size: 18px;
  font-weight: bold;
}

.completed-message i {
  margin-right: 10px;
  font-size: 20px;
}

.records-card {
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: none;
}

.header-controls {
  display: flex;
  gap: 10px;
}

.records-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.summary-card {
  flex: 1;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
}

.summary-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 10px;
}

.summary-label {
  font-size: 14px;
  opacity: 0.9;
}

.overtime {
  color: #f56c6c;
  font-weight: bold;
}

.clock-dialog-content {
  text-align: center;
}

.current-time-large {
  font-size: 36px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 10px;
  font-family: 'Courier New', monospace;
}

.current-date-large {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .mobile-menu-btn {
    display: flex;
  }

  .worker-sidebar {
    position: fixed;
    top: 0;
    left: -250px;
    width: 250px;
    height: 100vh;
    z-index: 1001;
    transition: all 0.3s ease;
  }

  .worker-sidebar.mobile-open {
    left: 0;
  }

  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .worker-main {
    padding: 80px 15px 20px 15px;
  }

  .time-display {
    font-size: 36px;
  }

  .clock-status {
    flex-direction: column;
    gap: 20px;
  }

  .status-divider {
    width: 2px;
    height: 20px;
    margin: 0;
  }

  .work-summary {
    flex-direction: column;
    gap: 10px;
  }

  .records-summary {
    flex-direction: column;
    gap: 10px;
  }

  .current-time-large {
    font-size: 28px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .header-actions {
    align-self: stretch;
  }

  .home-btn {
    width: 100%;
    justify-content: center;
  }

  .header-text h1 {
    font-size: 24px !important;
    background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%) !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
    background-clip: text !important;
    font-weight: 700 !important;
    text-shadow: 0 2px 4px rgba(103, 194, 58, 0.3) !important;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1)) !important;
    word-break: break-word;
    line-height: 1.3;
  }

  .header-text p {
    font-size: 14px !important;
    color: #6c757d !important;
    font-weight: 500 !important;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8) !important;
    word-break: break-word;
  }
}
</style>